import _ from "lodash";
import { useEffect, useMemo } from "react";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from "@mui/material";
import { Stack, useMediaQuery, useTheme } from "@mui/system";

import { useBoolean } from "src/hooks/use-boolean";

import { localStorageGetItem, localStorageRemoveItem } from "src/utils/local-storage";
import { sessionStorageGetItem, sessionStorageRemoveItem, sessionStorageSetItem } from "src/utils/session-storage";

import { useTranslate } from "src/locales";
import { rootApi } from "src/services/rootApi";
import { TAKEOVER_STORAGE_KEY, TakeoverState } from "src/services/system/system.types";
import { useAppDispatch } from "src/store/store";

import Iconify from "src/components/iconify";

export const TakeoverManager = () => {

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useAppDispatch();

  const takeoverSettingsControl = useBoolean(false);

  // Handle takeover handoff from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tempTakeoverKey = urlParams.get('takeover_token');

    if (tempTakeoverKey) {
      // Get takeover data from temporary localStorage key
      const takeoverData = localStorageGetItem(tempTakeoverKey);

      if (takeoverData) {
        // Move data to session storage for this tab
        sessionStorageSetItem(TAKEOVER_STORAGE_KEY, takeoverData);

        // Clean up temporary localStorage key
        localStorageRemoveItem(tempTakeoverKey);

        // Remove the temp_takeover parameter from URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('takeover_token');
        window.history.replaceState({}, '', newUrl.toString());

        // Set the takeover token for this session
        window.location.reload();
      }
    }
  }, []);

  const takeoverState: TakeoverState = useMemo(() => {

    // Check if the takeover state is available in session storage (tab-specific)
    const data = sessionStorageGetItem(TAKEOVER_STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }

    return null;
  }, []);

  const takeoverActive = useMemo(() => {

    // Check if the takeover state is available in session storage (tab-specific)
    const data = sessionStorageGetItem(TAKEOVER_STORAGE_KEY);
    if (data) {
      return true;
    }

    return false;
  }, []);

  if (!takeoverActive) {
    return null;
  }

  const exitTakeover = async () => {
    // Check if the takeover state is available in session storage (tab-specific)
    const data = sessionStorageGetItem(TAKEOVER_STORAGE_KEY);
    if (data) {
      // Remove the takeover state from session storage
      sessionStorageRemoveItem(TAKEOVER_STORAGE_KEY);
      dispatch(rootApi.util.resetApiState());
    }

    window.close();
  }

  if (isMobile) return null;

  return (
    <>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Iconify icon="eos-icons:admin" width={25} height={25} />}
        onClick={takeoverSettingsControl.onTrue}
      >
        {t('components.takeover_manager.button_label')}
      </Button>

      <Dialog
        open={takeoverSettingsControl.value}
        onClose={takeoverSettingsControl.onFalse}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            {t('components.takeover_manager.settings_modal.title')}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: 'background.paper' }}>
          <Stack direction="column" spacing={3}>
            {/* Tenant Information Section */}
            <Stack direction="column">
              <Typography variant="h6" color="text.primary" fontWeight="medium">
                {t('components.takeover_manager.settings_modal.viewing_tenant')}
              </Typography>
              <Typography variant="body2" color="text.primary" fontWeight="medium">
                {takeoverState?.tenant.company_name ?? 'Candidate Account'}
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={1}>
                Tenant ID: {takeoverState?.tenant.id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tenant Type: {_.capitalize(takeoverState?.tenant.type)}
              </Typography>
            </Stack>

            <Divider sx={{ my: 1 }} />

            {/* Account Information Section */}
            <Stack direction="column">
              <Typography variant="h6" color="text.primary" fontWeight="medium">
                {t('components.takeover_manager.settings_modal.account_details')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.primary">
                  {takeoverState?.account.first_name} {takeoverState?.account.last_name}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" mt={1}>
                Email: {takeoverState?.account.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Account ID: {takeoverState?.account.id}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={exitTakeover}
          >
            {t('components.takeover_manager.settings_modal.exit_takeover')}
          </Button>
          <Button
            variant="text"
            onClick={takeoverSettingsControl.onFalse}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>

  );
}